import React, { useState } from 'react';
import { Box, Typography, Chip, IconButton, Tooltip, Button, Divider, Checkbox } from '@mui/material';
import {
    CheckCircle,
    Cancel,
    Edit,
    History,
    Visibility,
    Inventory2,
    Build,
    TrendingUp,
    CalendarMonth,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { BundleCategory } from '../../types';
import { ImageThumb, ImageLightbox, LightboxSection } from '../carding/CardingImageComponents';
import SaleTagToggle, { SaleTagValue } from './SaleTagToggle';
import { BundleCogs, formatAsOfMonth, missingSkusLabel } from '../../utils/cogsFormat';
import { AnimatedInr } from '../common/CogsVisuals';
import { tokens } from '../../theme/tokens';

interface BundleCategoryCardProps {
    category: BundleCategory;
    inventoryMap: Record<string, number>;
    canBeMadeMap: Record<string, number>;
    onClick: () => void;
    onEdit: (e: React.MouseEvent) => void;
    formatDate: (dateString: string) => string;
    index: number;
    canEditSaleTag?: boolean;
    saleTagPending?: boolean;
    onSaleTagChange?: (parentSKU: string, next: SaleTagValue) => void;
    selected?: boolean;
    onSelectChange?: (parentSKU: string, selected: boolean) => void;
}

interface StatToneResult {
    color: string;
    bg: string;
}

// Semantic tone (not the brand accent) for a stat value — kept separate from the Salty
// orange so "this needs attention" reads independently of the page's own accent color.
function toneFor(kind: 'good' | 'warn' | 'bad' | 'neutral'): StatToneResult {
    switch (kind) {
        case 'good': return { color: '#16a34a', bg: 'rgba(34, 197, 94, 0.1)' };
        case 'warn': return { color: '#b45309', bg: 'rgba(245, 158, 11, 0.12)' };
        case 'bad': return { color: '#dc2626', bg: 'rgba(239, 68, 68, 0.1)' };
        default: return { color: '#6B625B', bg: 'rgba(100, 116, 139, 0.08)' };
    }
}

const StatTile: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string;
    tone: StatToneResult;
    tooltip?: string;
}> = ({ icon, label, value, tone, tooltip }) => {
    const content = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.15,
                px: 0.9,
                py: 0.5,
                borderRadius: '8px',
                bgcolor: tone.bg,
                minWidth: 0,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: tone.color, opacity: 0.85 }}>
                {icon}
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    {label}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: tone.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                {value}
            </Typography>
        </Box>
    );
    return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
};

const MetaLine: React.FC<{ brand?: string; category?: string; subCategory?: string; handle?: string }> = ({ brand, category, subCategory, handle }) => {
    const parts = [brand, category, subCategory].filter(Boolean);
    if (parts.length === 0 && !handle) return null;
    return (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.72rem' }}>
            {parts.join(' · ')}
            {handle && (
                <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'text.disabled', ml: parts.length ? 0.75 : 0 }}>
                    {parts.length ? `(${handle})` : handle}
                </Typography>
            )}
        </Typography>
    );
};

// COGS stat tile for the card: the bundle TOTAL (post-GST) shown in the same neutral visual
// language as the sibling Inventory / Can Be Made stat tiles — light neutral surface, standard
// border, an uppercase eyebrow and a large tabular-nums figure in ink that COUNTS UP on mount
// (reduced-motion → final value shown instantly, via AnimatedInr). Partial → a calm, muted
// 'N/M costed' indicator (no alarm/amber); frozen/as-of → muted chip; no-cost → muted indicator.
// Never the per-child breakdown (that lives in the Detailed view). Renders nothing when there's
// no cogs object.
const CogsStatTile: React.FC<{ cogs?: BundleCogs }> = ({ cogs }) => {
    if (!cogs) return null;

    const asOfMonth = formatAsOfMonth(cogs.asOf);
    const isEarliest = cogs.costBasis === 'post_gst_snapshot_earliest';
    const showAsOf = cogs.frozen === true || !!asOfMonth || isEarliest;
    const asOfLabel = isEarliest
        ? `as of ${asOfMonth || 'Jun 2026'} · earliest available`
        : asOfMonth ? `as of ${asOfMonth}` : 'frozen';
    const noCost = cogs.total === null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.35,
                px: 0.9,
                py: 0.65,
                mb: 0.6,
                borderRadius: '10px',
                border: `1px solid ${tokens.border}`,
                bgcolor: '#FAFAF7',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: tokens.muted }}>
                    COGS (post-GST)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {cogs.partial && !noCost && (
                        <Tooltip title={`${cogs.costedCount} of ${cogs.componentCount} components costed. Missing: ${missingSkusLabel(cogs.missingSkus)}`}>
                            <Typography component="span" sx={{ fontSize: '0.56rem', fontWeight: 700, color: tokens.muted, cursor: 'help', fontVariantNumeric: 'tabular-nums' }}>
                                {cogs.costedCount}/{cogs.componentCount} costed
                            </Typography>
                        </Tooltip>
                    )}
                    {showAsOf && !noCost && (
                        <Tooltip title={isEarliest ? 'Cost as of the earliest available snapshot (Jun 2026) — this version predates COGS coverage.' : ''}>
                            <Chip label={asOfLabel} size="small" sx={{ height: 16, fontSize: '0.54rem', fontWeight: 700, bgcolor: 'rgba(100,116,139,0.14)', color: '#4A433D', cursor: isEarliest ? 'help' : 'default', '& .MuiChip-label': { px: 0.6 } }} />
                        </Tooltip>
                    )}
                </Box>
            </Box>
            {noCost ? (
                <Tooltip title={cogs.missingSkus.length > 0 ? `No COGS found for: ${missingSkusLabel(cogs.missingSkus)}` : 'No COGS found'}>
                    <Typography component="span" sx={{ alignSelf: 'flex-start', fontSize: '0.8rem', fontWeight: 700, color: tokens.muted, cursor: 'help' }}>
                        No cost data
                    </Typography>
                </Tooltip>
            ) : (
                <AnimatedInr
                    value={cogs.total}
                    sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.05, color: tokens.ink, letterSpacing: '-0.01em' }}
                />
            )}
        </Box>
    );
};

const BundleCategoryCard: React.FC<BundleCategoryCardProps> = ({ category, inventoryMap, canBeMadeMap, onClick, onEdit, formatDate, index, canEditSaleTag = false, saleTagPending = false, onSaleTagChange, selected = false, onSelectChange }) => {
    const active = category.activeBundle;
    const inactive = category.latestInactiveVersion;
    const isActive = Boolean(active);
    const accent = isActive ? '#DC5829' : '#8A8078';

    const inventory = active ? inventoryMap[active.bundleSku] : undefined;
    const canBeMade = active ? canBeMadeMap[active.bundleSku] : undefined;
    const drr = active?.drr;
    const drrIsInherited = active && (active as any).drrSource === 'inherited';
    const hasDoc = active && drr !== undefined && drr !== null && drr > 0 && inventory !== undefined;
    const doc = hasDoc ? inventory! / drr! : null;

    const showCurrentLine = active && active.bundleSku !== category.parentSKU;

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const images = active?.images && active.images.length > 0
        ? active.images
        : (active?.imageURL && active.imageURL.startsWith('http') ? [active.imageURL] : []);

    return (
        <>
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, delay: Math.min(index, 12) * 0.03, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.99 }}
            style={{ height: '100%' }}
        >
            <Box
                onClick={onClick}
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    bgcolor: '#ffffff',
                    borderRadius: '14px',
                    border: '1px solid #ECECE4',
                    borderTop: `3px solid ${accent}`,
                    ...(selected ? { outline: '2px solid #DC5829', outlineOffset: 1 } : {}),
                    boxShadow: '0 1px 3px rgba(26, 20, 15, 0.05)',
                    transition: 'box-shadow 180ms ease, border-color 180ms ease',
                    '&:hover': {
                        borderColor: isActive ? 'rgba(220, 88, 41, 0.35)' : '#B8B2A6',
                        boxShadow: isActive
                            ? '0 14px 32px rgba(220, 88, 41, 0.16)'
                            : '0 14px 32px rgba(26, 20, 15, 0.1)',
                    },
                    p: 1.5,
                }}
            >
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        {canEditSaleTag && onSelectChange && (
                            <Checkbox
                                size="small"
                                checked={selected}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => onSelectChange(category.parentSKU, e.target.checked)}
                                inputProps={{ 'aria-label': `Select ${category.parentSKU}` }}
                                sx={{ p: 0.25, ml: -0.5, mr: -0.5, color: 'rgba(26, 20, 15, 0.35)', '&.Mui-checked': { color: '#DC5829' } }}
                            />
                        )}
                        <ImageThumb
                            images={images}
                            label={category.parentSKU}
                            size={34}
                            onClick={(e) => {
                                e?.stopPropagation();
                                if (images.length > 0) setLightboxOpen(true);
                            }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={700} noWrap sx={{ fontSize: '0.9rem', lineHeight: 1.2 }}>
                                {category.parentSKU}
                            </Typography>
                            {showCurrentLine && (
                                <Typography sx={{ fontSize: '0.65rem', color: '#DC5829', fontWeight: 600 }}>
                                    Now: {active!.bundleSku}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                        <Tooltip title="Edit bundle">
                            <IconButton size="small" onClick={onEdit} sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: '#DC5829', bgcolor: 'rgba(220, 88, 41, 0.08)' } }}>
                                <Edit sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                        <Chip
                            size="small"
                            icon={isActive ? <CheckCircle sx={{ fontSize: '13px !important' }} /> : <Cancel sx={{ fontSize: '13px !important' }} />}
                            label={isActive ? 'Active' : 'Inactive'}
                            sx={{
                                height: 20,
                                fontWeight: 600,
                                fontSize: '0.62rem',
                                bgcolor: isActive ? 'rgba(34, 197, 94, 0.1)' : '#F3F2EC',
                                color: isActive ? '#16a34a' : 'text.secondary',
                                '& .MuiChip-label': { px: 0.75 },
                            }}
                        />
                    </Box>
                </Box>

                {(canEditSaleTag || active?.saleTag || inactive?.saleTag) && (
                    <Box sx={{ mb: 0.9 }}>
                        <SaleTagToggle
                            value={active?.saleTag ?? inactive?.saleTag}
                            canEdit={canEditSaleTag}
                            loading={saleTagPending}
                            onChange={(next) => onSaleTagChange?.(category.parentSKU, next)}
                        />
                    </Box>
                )}

                {active ? (
                    <>
                        {(active.categoryCode || active.name) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                                {active.categoryCode && (
                                    <Chip size="small" label={active.categoryCode} sx={{ height: 18, bgcolor: 'rgba(220, 88, 41, 0.12)', color: '#B8451E', fontWeight: 700, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.6 } }} />
                                )}
                                {active.name && (
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.75rem' }} noWrap>
                                        {active.name}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <MetaLine brand={active.brand} category={active.category} subCategory={active.subCategory} handle={active.handle} />

                        {(active.saleType || active.plytix_id || (active.tags && active.tags.length > 0)) && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.35, mb: 0.75 }}>
                                {active.saleType && (
                                    <Chip size="small" label={active.saleType} sx={{ height: 18, bgcolor: 'rgba(220, 88, 41, 0.1)', color: '#B8451E', fontWeight: 600, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.6 } }} />
                                )}
                                {active.tags?.map((tag, idx) => (
                                    <Chip key={`${tag}-${idx}`} size="small" label={tag} sx={{ height: 18, bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#059669', fontWeight: 700, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.6 } }} />
                                ))}
                                {active.plytix_id && (
                                    <Chip
                                        size="small"
                                        label={`Plytix: ${active.plytix_id}`}
                                        sx={{ height: 18, bgcolor: 'rgba(34, 197, 94, 0.1)', color: '#16a34a', fontFamily: 'monospace', fontWeight: 600, fontSize: '0.58rem', '& .MuiChip-label': { px: 0.6 } }}
                                    />
                                )}
                            </Box>
                        )}

                        {/* Stat grid — replaces the old full-width colored bars */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mb: 0.75 }}>
                            <StatTile
                                icon={<Inventory2 sx={{ fontSize: 11 }} />}
                                label="Inventory"
                                value={inventory !== undefined ? String(inventory) : '—'}
                                tone={inventory === undefined ? toneFor('neutral') : inventory > 0 ? toneFor('good') : toneFor('bad')}
                            />
                            <StatTile
                                icon={<Build sx={{ fontSize: 11 }} />}
                                label="Can be Made"
                                value={canBeMade !== undefined ? String(canBeMade) : '—'}
                                tone={canBeMade === undefined ? toneFor('neutral') : canBeMade > 0 ? toneFor('good') : toneFor('bad')}
                            />
                            <StatTile
                                icon={drrIsInherited ? <History sx={{ fontSize: 11 }} /> : <TrendingUp sx={{ fontSize: 11 }} />}
                                label="DRR"
                                value={drr !== undefined && drr !== null ? drr.toFixed(2) : '—'}
                                tone={toneFor('neutral')}
                                tooltip={drrIsInherited ? `Carried over from v${(active as any).inheritedFromVersion} — this version's own sales data hasn't caught up yet` : undefined}
                            />
                            <StatTile
                                icon={<CalendarMonth sx={{ fontSize: 11 }} />}
                                label="DOC"
                                value={doc !== null ? `${doc.toFixed(1)}d` : '—'}
                                tone={doc === null ? toneFor('neutral') : doc < 7 ? toneFor('bad') : doc >= 14 ? toneFor('good') : toneFor('warn')}
                            />
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.68rem' }}>
                            {active.childs?.length || 0} child SKUs
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.75 }}>
                            {active.childs?.slice(0, 3).map((child) => (
                                <Chip key={child} label={child} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.6 } }} />
                            ))}
                            {(active.childs?.length || 0) > 3 && (
                                <Chip label={`+${(active.childs?.length || 0) - 3} more`} size="small" variant="outlined" color="secondary" sx={{ height: 18, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.6 } }} />
                            )}
                        </Box>

                        <Box sx={{ flex: 1 }} />
                        <CogsStatTile cogs={active.cogs ?? inactive?.cogs} />
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                            Created: {formatDate(active.createdAt)}
                        </Typography>
                    </>
                ) : (
                    <>
                        {inactive?.tags && inactive.tags.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.35, mb: 0.75 }}>
                                {inactive.tags.map((tag, idx) => (
                                    <Chip key={`${tag}-${idx}`} size="small" label={tag} sx={{ height: 18, bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#059669', fontWeight: 700, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.6 } }} />
                                ))}
                            </Box>
                        )}
                        {inactive && inactive.saleType && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.35, mb: 0.75 }}>
                                {inactive.saleType && (
                                    <Chip size="small" label={inactive.saleType} sx={{ height: 18, bgcolor: 'rgba(220, 88, 41, 0.1)', color: '#B8451E', fontWeight: 600, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.6 } }} />
                                )}
                            </Box>
                        )}
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                border: '1px dashed #DAD6CC',
                                borderRadius: '8px',
                                py: 1.5,
                                px: 1.5,
                                mb: 0.75,
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                No active bundle. Click to view history and enable.
                            </Typography>
                        </Box>
                        <CogsStatTile cogs={inactive?.cogs} />
                    </>
                )}

                <Divider sx={{ my: 0.75 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <History sx={{ fontSize: 14 }} color="disabled" />
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                            {category.totalVersions} version{category.totalVersions !== 1 ? 's' : ''}
                            {category.totalFutureVersions > 0 && (
                                <Typography component="span" variant="caption" sx={{ color: '#B8451E', ml: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                                    • {category.totalFutureVersions} Future
                                </Typography>
                            )}
                        </Typography>
                    </Box>
                    <Button size="small" endIcon={<Visibility sx={{ fontSize: 13 }} />} sx={{ minWidth: 0, py: 0.25, textTransform: 'none', color: '#DC5829', fontWeight: 600, fontSize: '0.72rem' }}>
                        View Details
                    </Button>
                </Box>
            </Box>
        </motion.div>
        <ImageLightbox
            open={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            sections={[{ title: category.parentSKU, images } as LightboxSection]}
        />
        </>
    );
};

export default BundleCategoryCard;
